import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../navigation/RootNavigator';
import { PageContainer, ContentContainer, Text, Theme } from '../../components/ui';
import { ExternalTransferService } from '../../services/externalTransferService';
import { loggingService } from '../../services/loggingService';
import { externalTransferStarted, externalTransferCompleted } from '../../utils/logging/actions';

type Props = NativeStackScreenProps<RootStackParamList, 'TransferAuth'>;

/**
 * TransferAuthScreen
 * 
 * Handles authentication transfer from external onboarding service.
 * 
 * Flow:
 * 1. Receives custom Firebase token from URL params
 * 2. Authenticates user with custom token
 * 3. Fetches user data from external service
 * 4. Imports data into Mentorist profile
 * 5. Navigates to abbreviated onboarding or directly to chat
 */
const TransferAuthScreen: React.FC<Props> = ({ route, navigation }) => {
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [progress, setProgress] = useState<string>('Connecting...');

  useEffect(() => {
    handleTransfer();
  }, []);

  const handleTransfer = async () => {
    try {
      // Extract token from multiple sources (in priority order)
      let token: string | undefined = route.params?.token; // 1. React Navigation params (native & some web cases)

      // 2. WEB FALLBACK: sessionStorage (token preserved in App.tsx before React Navigation stripped it)
      if (!token && Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
        const storedToken = sessionStorage.getItem('mentorist_transfer_token');
        if (storedToken) {
          token = storedToken;
          sessionStorage.removeItem('mentorist_transfer_token'); // One-time use
        }
      }

      // 3. FINAL FALLBACK: window.location (rare case where query params survive)
      if (!token && Platform.OS === 'web' && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        if (urlToken) {
          token = urlToken;
        }
      }

      if (!token) {
        console.error('❌ [TransferAuth] Transfer token is missing');
        throw new Error('Transfer token is missing. Please try again from the original service.');
      }

      // Step 1: Authenticate and load profile
      setProgress('Authenticating your account...');
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay for UX

      setProgress('Loading your profile...');

      const result = await ExternalTransferService.handleTransfer(token);

      if (!result.success || !result.profile) {
        throw new Error(result.error || 'Failed to transfer account');
      }
      
      setProgress('Welcome to Mentorist!');
      setStatus('success');

      // Wait a moment before navigation
      await new Promise(resolve => setTimeout(resolve, 800));

      // Log transfer completed with context
      loggingService.log(
        externalTransferCompleted(
          result.userId!,
          (result.profile?.onboardingAnswers?.answers?.length || 0) > 0,
          !!(result.profile?.coreProfile && (
            (result.profile.coreProfile.goals?.length || 0) > 0 ||
            (result.profile.coreProfile.interests?.length || 0) > 0 ||
            (result.profile.coreProfile.challenges?.length || 0) > 0
          ))
        )
      );

      // Navigate to abbreviated onboarding
      navigation.replace('AbbreviatedOnboarding', {
        profile: result.profile,
      });
      
    } catch (error: any) {
      // Log error to Firebase
      const { errorOccurred } = require('../../utils/logging/actions');
      loggingService.log(errorOccurred(
        error.message || 'Transfer screen error',
        'transfer_screen_error',
        {
          errorCode: error.code || 'unknown',
          errorName: error.name || 'Error',
          hasToken: !!route.params?.token,
        }
      ));
      
      setStatus('error');
      setErrorMessage(error.message || 'An unexpected error occurred during transfer.');
    }
  };


  const handleRetry = () => {
    setStatus('loading');
    setErrorMessage('');
    setProgress('Retrying...');
    handleTransfer();
  };

  const handleBackToLogin = () => {
    navigation.replace('Auth');
  };

  if (status === 'error') {
    return (
      <PageContainer>
        <ContentContainer>
          <View style={styles.centerContent}>
            <Text variant="heading2" align="center" style={styles.errorTitle}>
              Transfer Failed
            </Text>
            <Text variant="body1" color="secondary" align="center" style={styles.errorMessage}>
              {errorMessage}
            </Text>
            <View style={styles.buttonContainer}>
              <Text
                variant="body1"
                style={styles.linkButton}
                onPress={handleRetry}
              >
                Try Again
              </Text>
              <Text
                variant="body1"
                color="secondary"
                style={styles.linkButton}
                onPress={handleBackToLogin}
              >
                Back to Login
              </Text>
            </View>
          </View>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <View style={styles.centerContent}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
          <Text variant="heading3" align="center" style={styles.progressText}>
            {progress}
          </Text>
          <Text variant="body2" color="secondary" align="center" style={styles.subtitle}>
            {status === 'success' 
              ? 'Setting up your personalized experience...'
              : 'Please wait while we set up your account...'
            }
          </Text>
        </View>
      </ContentContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Theme.spacing.xl,
  },
  progressText: {
    marginTop: Theme.spacing.xl,
  },
  subtitle: {
    marginTop: Theme.spacing.md,
  },
  errorTitle: {
    marginBottom: Theme.spacing.lg,
  },
  errorMessage: {
    marginBottom: Theme.spacing.xxl,
  },
  buttonContainer: {
    gap: Theme.spacing.lg,
  },
  linkButton: {
    color: Theme.colors.primary,
    textDecorationLine: 'underline',
  },
});

export default TransferAuthScreen;

